import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowDown, MapPin, MessageCircle, Fish, Sparkles } from "lucide-react";
import { api, imageUrl, money } from "@/lib/api";
import { MaskedLine, Reveal } from "@/components/Motion";
import { ContactModal } from "@/components/ContactModal";
import { ClownfishGallery } from "@/components/ClownfishGallery";
import { ClownfishWaitlist } from "@/components/ClownfishWaitlist";
import { CLOWNFISH_IS_SAMPLE } from "@/lib/clownfish";
import { useCart } from "@/context/CartContext";
import { ASSETS } from "@/lib/brand";
import { WORK } from "@/lib/work";
import { toast } from "sonner";

const HOME_THUMBS = ["/work/work-66.jpg", "/work/work-01.jpg", "/work/work-03.jpg", "/work/work-39.jpg"];

const CHAPTERS = [
  { n: "01", t: "We grow, we don't just sell", d: "Every coral in our racks is aquacultured under controlled reef conditions — fragged, healed and colour-tested before it reaches your tank." },
  { n: "02", t: "Fish worth building a tank around", d: "Hand-selected exotic and designer specimens, quarantined and eating, so what arrives is a centrepiece, not a gamble." },
  { n: "03", t: "Maintenance without compromise", d: "White-glove servicing for private and commercial reef systems — water chemistry, dosing, and livestock health, handled." },
];

export default function Home() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const yImg = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const { add } = useCart();
  const [featured, setFeatured] = useState([]);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setFeatured(r.data.filter((p) => p.featured).slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
      {/* HERO */}
      <section ref={ref} className="relative min-h-screen flex items-end overflow-hidden pt-28 pb-16">
        <motion.div style={{ y }} className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(0,240,255,0.10),transparent_55%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_80%,rgba(255,0,127,0.10),transparent_55%)]" />
        </motion.div>

        <div className="max-w-[1400px] mx-auto px-5 md:px-10 w-full grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <Reveal><span className="mono-label">Exotic Reefing Inc — Saltwater Aquaculture</span></Reveal>
            <h1 className="wave-shimmer font-display text-[3.2rem] sm:text-7xl lg:text-[6rem] leading-[0.9] tracking-tight mt-6">
              <MaskedLine delay={0.1}>Living reefs,</MaskedLine>
              <MaskedLine delay={0.24}><span className="italic text-cyan">engineered</span> by</MaskedLine>
              <MaskedLine delay={0.38}>obsession.</MaskedLine>
            </h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 1 }}
              className="text-white/60 text-base md:text-lg max-w-xl mt-8 leading-relaxed">
              We grow rare exotic corals, curate show-stopping reef fish imported from Australia & Indonesia, and maintain the tanks that hold them. Browse the collection, or bid live on the pieces we let go.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
              className="flex flex-wrap gap-4 mt-10">
              <Link to="/shop" data-testid="hero-shop-btn" className="btn-pill btn-solid flex items-center gap-2">Shop Collection <ArrowUpRight size={16} /></Link>
              <Link to="/auctions" data-testid="hero-auction-btn" className="btn-pill btn-pink flex items-center gap-2">Live Auctions</Link>
              <button onClick={() => document.getElementById("clownfish")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-clownfish-btn" className="btn-pill border border-reefpink/40 text-reefpink hover:bg-reefpink/10 transition-colors flex items-center gap-2"><Fish size={16} /> See Our Clownfish</button>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.8 }}
              className="mt-4">
              <button onClick={() => setContactOpen(true)} data-testid="hero-contact-btn"
                className="btn-pill border border-cyan/40 text-cyan hover:bg-cyan/10 transition-colors inline-flex items-center gap-2">
                <MessageCircle size={16} /> Contact Us
              </button>
            </motion.div>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 0.8 }}
              data-testid="hero-pickup-note" className="mono-label text-cyan mt-6 flex items-center gap-2">
              <MapPin size={15} /> Free private local pickup — Miami / Hialeah
            </motion.p>
          </div>

          <motion.div style={{ y: yImg }} className="lg:col-span-5 flex justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.3, ease: [0.16, 1, 0.3, 1] }}
              className="relative">
              <motion.img
                src={ASSETS.logoRound} alt="Exotic Reefing Inc logo"
                animate={{ y: [0, -14, 0] }}
                transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
                className="w-[78vw] max-w-[460px] lg:max-w-[520px] rounded-full"
                style={{ filter: "drop-shadow(0 0 60px rgba(0,240,255,0.35))" }}
              />
            </motion.div>
          </motion.div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40">
          <span className="mono-label text-[0.6rem]">Scroll</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}><ArrowDown size={16} /></motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-white/10 py-5 bg-white/[0.015]">
        <Marquee speed={38} gradient={false}>
          {Array(6).fill(0).map((_, i) => (
            <span key={i} className="font-display italic text-2xl md:text-3xl text-white/30 mx-8">
              Exotic Corals <span className="text-cyan not-italic mx-3">✦</span> Clownfish Breeding Experts <span className="text-reefpink not-italic mx-3">✦</span> Exotic Clownfish In Stock <span className="text-cyan not-italic mx-3">✦</span> Imported from Australia & Indonesia <span className="text-reefpink not-italic mx-3">✦</span> Wholesale Available <span className="text-cyan not-italic mx-3">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* NEMO BREEDING ANNOUNCEMENT */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pt-16 md:pt-20" data-testid="nemo-breeding-banner">
        <Reveal>
          <div className="relative glass rounded-3xl border border-reefpink/30 overflow-hidden p-8 md:p-12"
            style={{ boxShadow: "0 30px 90px -40px rgba(255,0,127,0.45)" }}>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_20%,rgba(255,0,127,0.12),transparent_55%)]" />
            <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-reefpink/10 border border-reefpink/30 flex items-center justify-center shrink-0">
                  <Fish className="text-reefpink" size={26} />
                </div>
                <span className="mono-label text-reefpink inline-flex items-center gap-2">
                  <Sparkles size={14} /> Coming soon
                </span>
              </div>
              <div className="flex-1">
                <h2 className="font-display text-3xl md:text-4xl leading-tight mb-3">
                  We're breeding <span className="italic text-reefpink">exotic Nemos</span> 🐠
                </h2>
                <p className="text-white/60 leading-relaxed max-w-2xl mb-6">
                  Our exotic clownfish (Nemos) are currently being bred in-house and will be available for sale soon —
                  <b className="text-white/80"> individually</b>, as a <b className="text-white/80">bonded pair</b>, or as a
                  <b className="text-white/80"> ready-to-breed pair</b>. Join the waitlist for first dibs and we'll text or email you the moment they drop.
                </p>
                <ClownfishWaitlist />
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* CLOWNFISH SHOWCASE */}
      <section id="clownfish" className="max-w-[1400px] mx-auto px-5 md:px-10 pt-20 md:pt-28 scroll-mt-24" data-testid="clownfish-showcase">
        <Reveal>
          <span className="mono-label text-cyan">Clownfish breeding experts</span>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight mt-4 mb-4 max-w-2xl">
            Exotic <span className="italic text-cyan">clownfish</span> we love.
          </h2>
          <p className="text-white/55 max-w-2xl mb-4 leading-relaxed">
            A look at the designer clownfish morphs we breed and stock — from Picassos and Snowflakes to Black Storms,
            Frostbites and Platinums. Every fish is tank-bred, hardy and reef-safe.
          </p>
          {CLOWNFISH_IS_SAMPLE && (
            <p className="text-toxic/90 text-xs font-mono mb-10 border border-toxic/30 rounded-lg px-3 py-2 inline-block" data-testid="clownfish-sample-note">
              Variety showcase — photos update as new fish come in stock. Ask us what's available now.
            </p>
          )}
        </Reveal>
        <ClownfishGallery />
      </section>


      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-28 md:py-40">
        <Reveal><h2 className="font-display text-4xl md:text-6xl tracking-tight mb-20 max-w-2xl">Not a fish store. A <span className="italic text-cyan">reef laboratory.</span></h2></Reveal>
        <div className="space-y-16 md:space-y-24">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05}>
              <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-start border-t border-white/10 pt-10">
                <div className="md:col-span-3 font-mono text-6xl md:text-7xl text-white/10">{c.n}</div>
                <h3 className="md:col-span-5 font-display text-3xl md:text-4xl leading-tight">{c.t}</h3>
                <p className="md:col-span-4 text-white/55 leading-relaxed">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>


      {/* VIDEO HIGHLIGHT */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32 border-t border-white/10" data-testid="home-video-section">
        <Reveal>
          <span className="mono-label text-cyan">See it in motion</span>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight mt-4 mb-8 max-w-2xl">Inside a <span className="italic text-cyan">living reef.</span></h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="relative rounded-2xl overflow-hidden border border-cyan/20 card-glow">
            <video
              controls
              playsInline
              preload="metadata"
              poster="/reef-video-poster.jpg"
              className="w-full h-auto block bg-black"
              data-testid="home-video"
            >
              <source src="/reef-video.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10 flex flex-col items-center text-center gap-5">
            <p className="text-white/60 max-w-lg">Ready to bring a reef like this to life? We handle installs, upgrades and ongoing maintenance.</p>
            <div className="w-full max-w-md">
              <p className="mono-label text-cyan mb-3">As seen in our work</p>
              <div className="grid grid-cols-4 gap-2.5" data-testid="home-work-thumbs">
                {HOME_THUMBS.map((src, i) => (
                  <Link key={i} to="/services#gallery" aria-label="See our work" className="group relative aspect-square rounded-lg overflow-hidden border border-white/10 card-glow">
                    <img src={src} alt="Recent reef work by Exotic Reefing" loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-cyan/0 group-hover:bg-cyan/10 transition-colors" />
                  </Link>
                ))}
              </div>
            </div>
            <Link
              to="/services#book"
              data-testid="home-book-maintenance-cta"
              className="btn-pill btn-solid inline-flex items-center gap-2 text-base !px-8 !py-3.5"
            >
              <ArrowUpRight size={18} /> Book a Maintenance Visit
            </Link>
          </div>
        </Reveal>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-28">
          <div className="flex items-end justify-between mb-12">
            <Reveal><h2 className="font-display text-4xl md:text-5xl">Featured picks</h2></Reveal>
            <Link to="/shop" className="mono-label text-white/50 hover:text-cyan flex items-center gap-1">View all <ArrowUpRight size={14} /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <div className="group relative rounded-2xl overflow-hidden border border-white/10 card-glow">
                  <div className="aspect-[4/5] overflow-hidden">
                    <img src={imageUrl(p.image)} alt={p.name} className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-void to-transparent" />
                  </div>
                  <div className="absolute bottom-0 p-5 w-full flex items-end justify-between">
                    <div>
                      <h3 className="font-display text-2xl">{p.name}</h3>
                      <span className="font-mono text-cyan text-sm">{money(p.price)}</span>
                    </div>
                    <button onClick={() => { add(p); toast.success(`${p.name} added to cart`); }} className="btn-pill !py-2 !px-4 text-xs">Add</button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* SEO CONTENT */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32 border-t border-white/10">
        <Reveal>
          <span className="mono-label text-cyan">South Florida reef specialists</span>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-4 mb-8 max-w-3xl">
            Saltwater corals & exotic fish in <span className="italic text-cyan">South Florida</span>
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-8 md:gap-12">
          <Reveal delay={0.05}>
            <h3 className="font-display text-2xl mb-3">Exotic corals for sale</h3>
            <p className="text-white/55 leading-relaxed text-sm">
              From aquacultured zoanthids and chalices to bounce mushrooms and grafted montiporas, Exotic Reefing Inc grows and imports rare <b className="text-white/70">saltwater corals</b> from Australia and Indonesia. Every frag is healed, colored up and reef-ready before it ships. <Link to="/shop" className="text-cyan hover:underline">Browse the coral shop →</Link>
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h3 className="font-display text-2xl mb-3">Exotic saltwater fish</h3>
            <p className="text-white/55 leading-relaxed text-sm">
              Hand-selected, quarantined and eating — our <b className="text-white/70">exotic reef fish</b> include designer clownfish, fairy wrasses, tangs and other centerpiece specimens for your saltwater aquarium. As <b className="text-white/70">clownfish breeding experts</b>, we have <b className="text-white/70">exotic clownfish in stock for sale</b> — individually, as bonded pairs, or ready-to-breed pairs. Buy now or bid live in our <Link to="/auctions" className="text-cyan hover:underline">coral & fish auctions →</Link>
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <h3 className="font-display text-2xl mb-3">Aquarium maintenance & installs</h3>
            <p className="text-white/55 leading-relaxed text-sm">
              We provide professional <b className="text-white/70">reef tank maintenance</b>, water testing, equipment installation, new tank builds and full <b className="text-white/70">Neptune Apex programming</b> across South Florida — residential and commercial. <Link to="/services" className="text-cyan hover:underline">See services & pricing →</Link>
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <p className="text-white/45 leading-relaxed text-sm mt-12 max-w-4xl">
            Searching for <b className="text-white/60">saltwater aquariums near me</b>, exotic corals, or a reef tank specialist in Fort Lauderdale, Miami, Boca Raton or West Palm Beach? Exotic Reefing Inc is your local source for premium livestock, expert aquarium maintenance and Apex automation — turning reef tanks into masterpieces.
          </p>
        </Reveal>
      </section>


      {/* MAINTENANCE TEASER */}
      <section className="relative">
        <div className="grid md:grid-cols-2">
          <div className="relative h-[50vh] md:h-auto overflow-hidden">
            <img src="https://images.unsplash.com/photo-1519766030446-ae88fde27309" alt="Aquarium maintenance" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-void/30" />
          </div>
          <div className="bg-white/[0.02] p-10 md:p-20 flex flex-col justify-center border-l border-white/10">
            <Reveal>
              <span className="mono-label">Service</span>
              <h2 className="font-display text-4xl md:text-5xl mt-5 mb-6 leading-tight">Your reef, <span className="italic text-cyan">immaculate.</span></h2>
              <p className="text-white/55 mb-8 max-w-md leading-relaxed">From residential nano tanks to commercial displays, we handle water chemistry, dosing schedules, equipment and livestock health so your reef always looks its best.</p>
              <Link to="/services" data-testid="home-maintenance-btn" className="btn-pill w-fit flex items-center gap-2">Explore maintenance <ArrowUpRight size={15} /></Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
